import { Box, Button, Modal, Typography, SelectChangeEvent, Alert, SxProps } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  CurrencyConfigRankedRes,
  useAddUserWithdrawAddressMutation,
  useGetCryptoCurrenciesQuery,
  useGetUserDatav2Query,
  useVerfiyUserWithdrawAddressEmailMutation,
} from "src/store/features/api/generated";
import { formatAddressCheck } from "src/utils/helpers/CryptoAddressFormatter";
import { AddressTestingStyles } from "../common/styles/AddressTesting.styles";
import { AddressForm } from "./AddressForm";
import AddressVerify from "./AddressVerify";
import CloseIcon from "@mui/icons-material/Close";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import { Theme } from "@mui/system";
import clsx from "clsx";
import AddressSuccess from "./AddressSuccess";
import AddIcon from "@mui/icons-material/Add";
import {
  setAddress,
  setSelectedCurrency,
  setMemo,
  setStep,
  useAddressTestingState,
  setModalOpen,
  setTrFields,
  resetTrFields,
} from "src/store/features/addressTesting.slice";
import AddressTrOwnership from "./AddressTrOwnership";
import AddressTrKind from "./AddressTrKind";
import { useTrEnabled } from "src/utils/hooks/useTrEnabled";
import { useAppDispatch } from "src/store/store";
import { trackEvent } from "src/Amplitude";
import AddressFooterButtons from "./AddressBtnGroup";
import usePermission from "src/utils/hooks/usePermission";
import { Link } from "react-router-dom";
type Props = { refetchAddress: () => void };

const mobileStyle: SxProps<Theme> = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  bgcolor: "background.paper",
  borderRadius: "10px",
};

const desktopStyle: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "auto",
  transform: "translate(-50%, -50%)",
  width: "500px",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "10px",
  display: "flex",
  flexDirection: "column",
};
const AddressTesting = (props: Props) => {
  const { refetchAddress } = props;

  // ---------------Sytles------------------------
  const classes = AddressTestingStyles();
  const isMobile = useIsMobile();
  const style = isMobile ? mobileStyle : desktopStyle;

  //----------------Redux-----------------------
  const dispatch = useAppDispatch();
  const { address, selectedCurrency, memo, step, modalOpen, tr } = useAddressTestingState();
  // D3 — disable Save when the third-party block fires inside ownership step.
  const [trBlocked, setTrBlocked] = useState(false);

  // ---------------State------------------------
  const [emailCode, setEmailCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [selectedNetwork, setSelectedNetwork] = React.useState<string | null>("");
  const [depositableCurrencies, setDepositableCurrencies] = useState<CurrencyConfigRankedRes[]>([]);
  const [twoFactorVerified, setTwoFactorVerified] = useState(false);
  const [loading2fa, setLoading2fa] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState("");
  const [twoFactorIsError, setTwoFactorIsError] = useState(false);
  const [addressAddType, setAddressAddType] = useState("");
  const [walletNickname, setWalletNickname] = useState("");
  const [walletNicknameWarning, setWalletNicknameWarning] = useState("");

  // After D3, the verify step is step 4.
  const disableTwoFactorButton = twoFactorCode.length < 6 && step === 4;
  const modalTitle = (() => {
    if (step === 2) return "Wallet Ownership";
    if (step === 3) return "Wallet Type";
    if (step === 4) return "Email & 2FA Security";
    return "Add Address";
  })();
  // ------------ERROR STATES-------------
  const [addressWarning, setAddressWarning] = React.useState("");
  const [currencyError, setCurrencyError] = React.useState("");
  const ERROR_DUPLICATE_VERIFIED =
    "This address is already being added to your account, you may request for a withdrawal from your Broker." as const;
  const ERROR_DUPLICATE_UNVERIFIED =
    "This address is already being added to your account. Once verified, you may request for a withdrawal from your Broker." as const;

  // ---------------RTKQ--------------------------
  const { data: cryptoCoinsData } = useGetCryptoCurrenciesQuery();

  const [
    addAddress,
    { isLoading: addressDataIsLoading, error: addAddressError, reset, isError: addAddressIsError },
  ] = useAddUserWithdrawAddressMutation();

  const [submitAddress, { isLoading: submittingAddress }] =
    useVerfiyUserWithdrawAddressEmailMutation();

  const { data: user } = useGetUserDatav2Query();
  const { isGuest } = usePermission();
  // Master TR kill-switch — when off, skip steps 2 (TR ownership) and 3 (TR
  // kind) entirely. step 1 advances straight to step 4 (2FA) after posting
  // the address without TR fields. Backend deposit/withdrawal services are
  // also gated, so the address record is fine without TR metadata while the
  // flag is off — the D4 backfill flow takes over when it's flipped on.
  const trOn = useTrEnabled("OUTBOUND");
  // ---------------_CONST------------------------
  const BTN_TITLE = "New Address" as const;
  // ---------------Helpers------------------------
  const handleOpen = () => {
    dispatch(setModalOpen(true));
    trackEvent("Address_Testing_Modal_Opened");
  };
  const handleClose = () => {
    dispatch(setModalOpen(false));
    dispatch(setStep(1));
    dispatch(setAddress(""));
    setAddressWarning("");
    setEmailVerified(false);
    setCurrencyError("");
    dispatch(setSelectedCurrency(undefined));
    dispatch(resetTrFields());
    setTrBlocked(false);
    reset();
  };

  const handleAddressChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newAddress = event.target.value;
    dispatch(setAddress(newAddress));
    if (newAddress === "") {
      setAddressWarning("Address required.");
    } else if (selectedCurrency && !formatAddressCheck(selectedCurrency.code, newAddress)) {
      setAddressWarning("Please ensure the address format is correct.");
    } else {
      setAddressWarning("");
    }
  };

  const handleCurrencyChange = (event: SelectChangeEvent<string>) => {
    const selectedCurrencyCode = event.target.value;
    const selectedCurrency = depositableCurrencies.find(
      (currency) => currency.code === selectedCurrencyCode,
    );
    dispatch(setSelectedCurrency(selectedCurrency));
    if (selectedCurrency) {
      setSelectedNetwork(selectedCurrency ? selectedCurrency.networkName : null);
      trackEvent("Address_Testing_Selected_Currency", { selectedCurrency });
    } else {
      setSelectedNetwork("");
    }
  };

  const handleNicknameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const newValue = event.target.value;

    // Only allow alphanumeric characters (letters, numbers, and spaces)
    const alphanumericValue = newValue.replace(/[^a-zA-Z0-9\s]/g, "");

    // Check if the input exceeds the character limit before truncating
    const exceedsLimit = alphanumericValue.length > 25;

    // Limit to 25 characters
    const validatedValue = alphanumericValue.slice(0, 25);

    // Update state with the validated value
    setWalletNickname(validatedValue);

    // Set warning if the input exceeds the character limit
    if (exceedsLimit) {
      setWalletNicknameWarning("Nickname must be 25 characters or less");
    } else {
      // Clear warning if input is valid
      setWalletNicknameWarning("");
    }

    // If validation changed the input, update the input field value directly
    if (newValue !== validatedValue) {
      event.target.value = validatedValue;
    }
  };

  const handleNetworkChange = (event: SelectChangeEvent<string>) => {
    setSelectedNetwork(event.target.value);
  };

  const handleAddAnother = () => {
    dispatch(setSelectedCurrency(undefined));
    dispatch(setAddress(""));
    dispatch(resetTrFields());
    setTrBlocked(false);
    setEmailVerified(false);
    dispatch(setStep(1));
  };

  /**
   * POST the address with whatever TR fields are currently in slice state.
   * When the master TR flag is off, the caller skips TR steps and these
   * fields are all `undefined` — the backend accepts that and the address
   * row goes in without TR metadata (D4 backfill will collect it later if
   * the flag flips on).
   */
  const submitAddAddress = async (): Promise<boolean> => {
    const formatName = selectedCurrency?.formatName;
    const code = selectedCurrency?.code;
    if (!formatName || !code) {
      console.warn("Format Name or Code is missing.");
      return false;
    }
    try {
      await addAddress({
        addWithdrawAddressDto: {
          address,
          format: formatName,
          token: code,
          memo: memo ? memo : undefined,
          nickname: walletNickname,
          // Travel-Rule fields — populated when steps 2/3 ran, all undefined
          // when the master flag suppressed them.
          isFirstParty: tr.isFirstParty ?? undefined,
          thirdPartyRecipientName: tr.thirdPartyRecipientName,
          destinationType: tr.destinationType,
          destinationVasp: tr.destinationVasp,
          destinationVaspName: tr.destinationVaspName,
          walletVerificationReference: tr.walletVerificationReference,
        } as any,
      })
        .unwrap()
        .then(() => {
          refetchAddress();
        });
      return true;
    } catch (error) {
      console.warn(error);
      return false;
    }
  };

  const handleNextClick = async () => {
    if (step === 1) {
      // Form validation. D3 defers the actual POST until step 3 so the TR
      // fields can ride along on the same request. When the master TR flag
      // is off, we skip steps 2/3 and post-then-jump straight to step 4.
      let hasErrors = false;

      if (!selectedCurrency) {
        setCurrencyError("Please select a currency.");
        hasErrors = true;
      } else {
        setCurrencyError("");
      }

      if (!address) {
        setAddressWarning("Address required.");
        hasErrors = true;
      } else {
        setAddressWarning("");
      }

      if (hasErrors) return;

      if (!trOn) {
        const ok = await submitAddAddress();
        if (!ok) return;
        dispatch(setStep(4));
        return;
      }

      dispatch(setStep(2));
    } else if (step === 2) {
      // D3.1 — ownership. Block flag is enforced inline by the component;
      // we mirror it here so Save stays disabled until the user changes
      // their answer or the flag flips.
      if (trBlocked) return;
      if (tr.isFirstParty === null) return;
      if (tr.isFirstParty === false && !(tr.thirdPartyRecipientName || "").trim()) return;
      dispatch(setStep(3));
    } else if (step === 3) {
      // D3.2 — kind + the actual POST including TR fields.
      if (!tr.destinationType) return;
      if (tr.destinationType === "Self-Hosted Wallet" && !tr.walletVerificationReference) return;
      if (tr.destinationType === "Exchange" && !tr.destinationVasp) return;
      const ok = await submitAddAddress();
      if (ok) dispatch(setStep(4));
    } else if (step === 4) {
      if (!user?.details.email) {
        setTwoFactorError("Could not verify email. Please contact support");
        return;
      }

      if (twoFactorCode && twoFactorCode.length === 6) {
        trackEvent("Address_Testing_Email2fa_Entered");
        setLoading2fa(true);
        try {
          await submitAddress({
            addressWithCodesDto: {
              address: address,
              emailCode: emailCode,
              twoFactorCode: twoFactorCode,
            },
          })
            .unwrap()
            .then((resp) => {
              setAddressAddType(resp.status);
              setTwoFactorVerified(true);
              setTwoFactorIsError(false);
              setLoading2fa(false);
              dispatch(setStep(5));
              trackEvent("Address_Testing_Submitted");
              refetchAddress();
            });
        } catch (error) {
          setLoading2fa(false);
          setTwoFactorIsError(true);
          setTwoFactorError("Something went wrong submitting address");
        }
      }
    }
  };
  // ---------------Effects------------------------

  useEffect(() => {
    if (cryptoCoinsData) {
      const depositableData = cryptoCoinsData.filter(
        (item) => item.permissions && item.permissions.withdraw,
      );
      setDepositableCurrencies(depositableData);
    }
  }, [cryptoCoinsData]);

  // FORMAT BACKEND ERRORS HERE!
  const ErrorHandler = ({ currencyError }: { currencyError?: string }) => {
    let errorAlert;

    if (!currencyError) return null;
    switch (currencyError) {
      case "This address has already been added and is pending.":
        errorAlert = (
          <Alert sx={{ width: "100%", marginBottom: "10px" }} severity="error">
            {ERROR_DUPLICATE_UNVERIFIED}
          </Alert>
        );
        break;
      case "User doesn't have 2FA set up!":
        errorAlert = (
          <Alert sx={{ width: "100%", marginBottom: "10px" }} severity="error">
            <span>{`It looks like you don't have two factor enabled, `}</span>
            <Link
              onClick={() =>
                trackEvent(
                  "Link_Click",
                  {
                    link: "Click_Here_Enable_2FA",
                  },
                  "Address_Testing",
                )
              }
              to={"/settings/security"}>
              <span>{"click here"}</span>
            </Link>
          </Alert>
        );
        break;
      case "Address is already associated with this currency. Please use a different address.":
        errorAlert = (
          <Alert sx={{ width: "100%", marginBottom: "10px" }} severity="success">
            {ERROR_DUPLICATE_VERIFIED}
          </Alert>
        );
        break;
      case "Authentication error: Session Expired":
        errorAlert = (
          <Alert sx={{ width: "100%", marginBottom: "10px" }} severity="error">
            {"Session Expired"}
          </Alert>
        );
        break;
      default:
        errorAlert = (
          <Alert sx={{ width: "100%", marginBottom: "10px" }} severity="error">
            {currencyError}
          </Alert>
        );
    }

    return errorAlert;
  };

  return (
    <>
      {!isGuest && (
        <Button
          disableRipple
          onClick={handleOpen}
          className={classes.dashedButton}
          startIcon={<AddIcon />}>
          <Typography>{BTN_TITLE}</Typography>
        </Button>
      )}

      <Modal
        open={modalOpen}
        onClose={handleClose}
        aria-labelledby="address-modal-title"
        aria-describedby="address-modal-description">
        <Box sx={style}>
          <Box
            className={clsx(classes.titleContainer, {
              [classes.titleContainerDesktop]: !isMobile,
            })}>
            <CloseIcon onClick={handleClose} className={classes.closeIcon} />
            <Typography id="modal-title" className={classes.modalTitle}>
              {modalTitle}
            </Typography>
            <div></div>
          </Box>

          <Box
            className={clsx(classes.mainContainerMobile, {
              [classes.mainContainerDesktop]: !isMobile,
            })}>
            {step === 1 && (
              <AddressForm
                handleNetworkChange={handleNetworkChange}
                selectedCurrency={selectedCurrency}
                depositableCurrencies={depositableCurrencies}
                handleCurrencyChange={handleCurrencyChange}
                currencyError={currencyError}
                address={address}
                setAddressWarning={setAddressWarning}
                handleAddressChange={handleAddressChange}
                addressWarning={addressWarning}
                selectedNetwork={selectedNetwork}
                memo={memo}
                handleMemoChange={setMemo}
                walletNickname={walletNickname}
                walletNicknameWarning={walletNicknameWarning}
                handleNicknameChange={handleNicknameChange}
              />
            )}
            {step === 2 && trOn && (
              <AddressTrOwnership
                value={{
                  isFirstParty: tr.isFirstParty,
                  thirdPartyRecipientName: tr.thirdPartyRecipientName,
                }}
                onChange={(next) => dispatch(setTrFields(next))}
                onBlocked={setTrBlocked}
                // Closes the address-add modal when the user cancels from the
                // third-party block dialog — the dialog otherwise has no way
                // to dismiss itself in this flow (its default cancel resets
                // the unrelated withdrawal slice).
                onCancelAddressFlow={handleClose}
              />
            )}
            {step === 3 && trOn && (
              <AddressTrKind
                asset={selectedCurrency?.code}
                value={{
                  destinationType: tr.destinationType ?? null,
                  destinationVasp: tr.destinationVasp,
                  destinationVaspName: tr.destinationVaspName,
                  walletVerificationReference: tr.walletVerificationReference,
                }}
                onChange={(next) =>
                  dispatch(
                    setTrFields({
                      destinationType: next.destinationType ?? undefined,
                      destinationVasp: next.destinationVasp,
                      destinationVaspName: next.destinationVaspName,
                      walletVerificationReference: next.walletVerificationReference,
                    }),
                  )
                }
              />
            )}
            {step === 4 && (
              <AddressVerify
                submitAddress={submitAddress}
                twoFactorIsError={twoFactorIsError}
                twoFactorError={twoFactorError}
                twoFactorVerified={twoFactorVerified}
                loading2fa={loading2fa}
                setEmailCode={setEmailCode}
                emailCode={emailCode}
                setTwoFactorIsError={setTwoFactorIsError}
                email={user?.details.email}
                emailVerified={emailVerified}
                twoFactorCode={twoFactorCode}
                setTwoFactorCode={setTwoFactorCode}
                address={address}
                setEmailVerified={setEmailVerified}
                setStep={setStep}
              />
            )}
            {step === 5 && (
              <AddressSuccess
                address={address}
                name={selectedCurrency?.name}
                format={selectedCurrency?.formatName}
                memo={memo}
                addressAddType={addressAddType}
                refetchAddress={refetchAddress}
              />
            )}
            {addAddressError && <ErrorHandler currencyError={addAddressError.message} />}
            <AddressFooterButtons
              step={step}
              isLoading={addressDataIsLoading || submittingAddress}
              // Mirror the validation gates inside handleNextClick so the Next
              // button is *visually* disabled when the form isn't ready —
              // otherwise the click silently no-ops on TR steps and the user
              // gets no feedback on what's missing.
              disabled={
                addAddressIsError ||
                disableTwoFactorButton ||
                (step === 2 &&
                  (tr.isFirstParty === null ||
                    trBlocked ||
                    (tr.isFirstParty === false &&
                      !(tr.thirdPartyRecipientName || "").trim()))) ||
                (step === 3 &&
                  (!tr.destinationType ||
                    (tr.destinationType === "Self-Hosted Wallet" &&
                      !tr.walletVerificationReference) ||
                    (tr.destinationType === "Exchange" && !tr.destinationVasp)))
              }
              emailVerified={emailVerified}
              handleAddAnother={handleAddAnother}
              handleNextClick={handleNextClick}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AddressTesting;
